import { fetchAuthSession } from 'aws-amplify/auth';
import type {
    SupplierAdminService,
    SupplierMember,
    SupplierEntity,
    MemberRequestResult,
} from '../types';
import { getAwsConfig } from '../../config/appConfig';
import { ensureAmplifyConfigured } from './amplifyConfig';

/**
 * Supplier-admin self-service service. Backs the Supplier Users screen for a
 * supplier admin. The supplier, role, and domain are resolved server-side from the
 * token; the UI only sends the invitee email. All routes are Cognito-authorized
 * and further gated to MASTER on the server.
 */

async function masterUrl(path: string): Promise<string> {
    const base = getAwsConfig().asnApiEndpoint;
    if (!base) {
        throw new Error('ASN API endpoint is not configured.');
    }
    return `${base.replace(/\/$/, '')}/v1/supplier-admin${path}`;
}

async function authHeader(): Promise<Record<string, string>> {
    const session = await fetchAuthSession();
    const token = session.tokens?.idToken?.toString();
    if (!token) {
        throw new Error('Not authenticated.');
    }
    return { Authorization: token };
}

/** Pull an RFC 7807 `detail` from an error response, falling back to a default. */
async function errorDetail(res: Response, fallback: string): Promise<string> {
    try {
        const body = await res.json();
        if (body && typeof body.detail === 'string') return body.detail;
    } catch {
        // ignore
    }
    return fallback;
}

export const awsSupplierAdminService: SupplierAdminService = {
    async listMembers(): Promise<SupplierMember[]> {
        ensureAmplifyConfigured();
        const res = await fetch(await masterUrl('/members'), { headers: await authHeader() });
        if (!res.ok) {
            throw new Error(await errorDetail(res, `Failed to load members: ${res.status}`));
        }
        const body = (await res.json()) as {
            members: SupplierMember[];
            pendingRequests?: { email: string }[];
        };
        // Append the master's own pending requests as display-only rows so they
        // can see who they've requested while it awaits approval.
        const pending: SupplierMember[] = (body.pendingRequests ?? []).map((r) => ({
            username: '',
            email: r.email,
            role: 'MEMBER',
            enabled: false,
            status: 'PENDING_APPROVAL',
            pending: true,
        }));
        return [...body.members, ...pending];
    },

    async requestMember(email: string): Promise<MemberRequestResult> {
        ensureAmplifyConfigured();
        const res = await fetch(await masterUrl('/members'), {
            method: 'POST',
            headers: { ...(await authHeader()), 'Content-Type': 'application/json' },
            body: JSON.stringify({ email }),
        });
        if (!res.ok) {
            throw new Error(await errorDetail(res, `Failed to request member: ${res.status}`));
        }
        return (await res.json()) as MemberRequestResult;
    },

    async disableMember(username: string): Promise<void> {
        ensureAmplifyConfigured();
        const res = await fetch(
            await masterUrl(`/members/${encodeURIComponent(username)}/disable`),
            { method: 'POST', headers: await authHeader() },
        );
        if (!res.ok) {
            throw new Error(await errorDetail(res, `Failed to disable member: ${res.status}`));
        }
    },

    async enableMember(username: string): Promise<void> {
        ensureAmplifyConfigured();
        const res = await fetch(
            await masterUrl(`/members/${encodeURIComponent(username)}/enable`),
            { method: 'POST', headers: await authHeader() },
        );
        if (!res.ok) {
            throw new Error(await errorDetail(res, `Failed to enable member: ${res.status}`));
        }
    },

    async listEntities(): Promise<SupplierEntity[]> {
        ensureAmplifyConfigured();
        const res = await fetch(await masterUrl('/entities'), { headers: await authHeader() });
        if (!res.ok) {
            throw new Error(await errorDetail(res, `Failed to load entities: ${res.status}`));
        }
        const body = (await res.json()) as { entities: SupplierEntity[] };
        return body.entities;
    },
};

export default awsSupplierAdminService;
